import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import SourceCell from '@/components/admin/SourceCell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  bulkActionStudents,
  commitStudentImport,
  createStudent,
  deleteStudent,
  getAgents,
  getStudents,
  getStudentsExportUrl,
  getStudentsMeta,
  previewStudentImport,
  triggerDownload,
  updateStudent,
} from '@/lib/api';
import { formatDisplayDate } from '@/lib/dates';
import { heardAboutSortValue } from '@/lib/heardAbout';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Copy,
  Download,
  Eye,
  FileSpreadsheet,
  FileText,
  Loader2,
  MoreHorizontal,
  Plus,
  Trash2,
  Upload,
  Users,
  Wallet,
} from 'lucide-react';

const lifecycleOptions = [
  { value: 'all', label: 'All students' },
  { value: 'attention_needed', label: 'Needs attention' },
  { value: 'graduating_soon', label: 'Graduating soon' },
  { value: 'notification_pending', label: 'Notification pending' },
  { value: 'international', label: 'International' },
  { value: 'compliance_alert', label: 'Compliance alert' },
  { value: 'deposit_overdue', label: 'Deposit overdue' },
  { value: 'graduation_ready', label: 'Graduation ready' },
];

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'not_needed', label: 'Not needed' },
];

// Sentinel for the "not set" choice in the inline enrollment-status select
// (the backend stores an empty string for unset enrollment status).
const NONE_VALUE = '__none__';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const inputClass =
  'border-[rgba(6,43,26,0.16)] focus-visible:ring-[3px] focus-visible:ring-[rgba(212,175,55,0.35)] focus-visible:ring-offset-0 focus-visible:border-[#D4AF37]';
const importHeaders = ['name', 'email', 'phone', 'dob', 'gender', 'program', 'start_date', 'enrollment_status', 'campus', 'delivery_method'];
const PROGRAM_OPTIONS = [
  'French as a Second Language (FSL)',
  'French Test Preparation (TCF & TEF)',
  'International Esports Management Diploma',
  'Hospitality Operations Diploma',
  'Global Management in Hospitality Diploma',
];

const emptyAddForm = {
  name: '',
  email: '',
  phone: '',
  dob: '',
  gender: '',
  program_name: NONE_VALUE,
  start_date: '',
  enrollment_status: NONE_VALUE,
  campus: 'Undecided',
  delivery_method: NONE_VALUE,
  agent_id: NONE_VALUE,
};

const emptyImportState = {
  step: 'upload',
  file: null,
  preview: null,
  result: null,
  error: '',
};

function formatDate(value) {
  return formatDisplayDate(value, { fallback: 'Not set' });
}

function badgeClass(tone) {
  if (tone === 'danger') return 'border-red-200 bg-red-50 text-red-700 hover:bg-red-50';
  if (tone === 'warning') return 'border-[#D4AF37]/35 bg-[#D4AF37]/10 text-[#8A6508] hover:bg-[#D4AF37]/10';
  if (tone === 'success') return 'border-green-200 bg-green-50 text-green-700 hover:bg-green-50';
  return 'border-[#062B1A]/12 bg-[#062B1A]/5 text-[#062B1A]/70 hover:bg-[#062B1A]/5';
}

function studentInitials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'ST';
}

function cleanError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

function csvCell(value) {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function downloadStudentTemplate() {
  const example = [
    'Taylor Example',
    'taylor.example+fake@example.com',
    '+1 604 555 0100',
    '2001-04-12',
    'Prefer not to say',
    'Hospitality Operations Diploma',
    '2026-09-01',
    'Admitted',
    'Vancouver (Downtown)',
    'In Person',
  ];
  const csv = [importHeaders, example].map((row) => row.map(csvCell).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'student_import_template.csv';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function selectPayload(value) {
  return value === NONE_VALUE ? '' : value;
}

export default function ManageStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('all');
  const [programFilter, setProgramFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pendingDelete, setPendingDelete] = useState(null);
  const [savingInline, setSavingInline] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [studentMeta, setStudentMeta] = useState({ enrollment_status: [], campus: [], delivery_method: [], programs: PROGRAM_OPTIONS });
  const [agents, setAgents] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [addErrors, setAddErrors] = useState({});
  const [addingStudent, setAddingStudent] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importState, setImportState] = useState(emptyImportState);
  const [previewingImport, setPreviewingImport] = useState(false);
  const [committingImport, setCommittingImport] = useState(false);
  const loadRequestId = useRef(0);

  useEffect(() => {
    const requestId = ++loadRequestId.current;
    const timeoutId = window.setTimeout(() => {
      loadStudents(requestId);
    }, 250);
    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, lifecycleFilter]);

  // Metadata is non-critical for the table, but powers the create modal selects.
  useEffect(() => {
    getStudentsMeta()
      .then((res) => setStudentMeta({
        enrollment_status: Array.isArray(res.data?.enrollment_status) ? res.data.enrollment_status : [],
        campus: Array.isArray(res.data?.campus) ? res.data.campus : [],
        delivery_method: Array.isArray(res.data?.delivery_method) ? res.data.delivery_method : [],
        programs: Array.isArray(res.data?.programs) ? res.data.programs : PROGRAM_OPTIONS,
      }))
      .catch(() => {});
    getAgents()
      .then((res) => setAgents(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const loadStudents = async (requestId = ++loadRequestId.current) => {
    if (requestId !== loadRequestId.current) return;
    try {
      setLoading(true);
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (lifecycleFilter !== 'all') params.lifecycle_filter = lifecycleFilter;
      const { data } = await getStudents(params);
      if (requestId !== loadRequestId.current) return;
      setStudents(data || []);
      setSelectedIds((prev) => {
        const available = new Set((data || []).map((student) => student.id));
        return new Set([...prev].filter((id) => available.has(id)));
      });
    } catch (err) {
      if (requestId !== loadRequestId.current) return;
      console.error(err);
      toast.error('Could not load students');
    } finally {
      if (requestId === loadRequestId.current) setLoading(false);
    }
  };

  const programOptions = useMemo(() => {
    const programs = [...new Set(students.map((student) => student.program_name).filter(Boolean))].sort();
    return [{ value: 'all', label: 'All programs' }, ...programs.map((program) => ({ value: program, label: program }))];
  }, [students]);

  const filteredStudents = useMemo(() => {
    return students.filter((student) => {
      if (programFilter !== 'all' && student.program_name !== programFilter) return false;
      if (statusFilter !== 'all' && student.graduation_notification_status !== statusFilter) return false;
      return true;
    });
  }, [students, programFilter, statusFilter]);

  const summary = useMemo(() => ({
    total: students.length,
    visible: filteredStudents.length,
    attention: students.filter((student) => student.lifecycle_flags?.attention_needed).length,
    depositOverdue: students.filter((student) => student.lifecycle_flags?.deposit_overdue).length,
  }), [filteredStudents.length, students]);

  const selectedStudents = useMemo(
    () => students.filter((student) => selectedIds.has(student.id)),
    [selectedIds, students],
  );

  const agentOptions = useMemo(() => (
    agents
      .map((agent) => ({
        value: agent.id,
        label: [agent.company_name || agent.name, agent.email].filter(Boolean).join(' - ') || agent.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [agents]);

  const exportStudents = async (format, ids) => {
    const idArray = Array.isArray(ids) ? ids : [...(ids || [])];
    try {
      await triggerDownload(
        getStudentsExportUrl(format, idArray),
        idArray.length ? `students_selected.${format}` : `students_export.${format}`,
      );
      toast.success(`Downloaded ${idArray.length || 'all'} student${idArray.length === 1 ? '' : 's'} as ${format.toUpperCase()}`);
    } catch (err) {
      console.error(err);
      toast.error(`Could not download ${format.toUpperCase()} export`);
    }
  };

  const handleInlineUpdate = async (student, payload, label) => {
    try {
      setSavingInline(`${student.id}:${label}`);
      const { data } = await updateStudent(student.id, payload);
      setStudents((prev) => prev.map((item) => (item.id === student.id ? data : item)));
      toast.success(`Updated ${student.name}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || `Could not update ${label}`);
      await loadStudents();
    } finally {
      setSavingInline('');
    }
  };

  const confirmSingleDelete = (student) => {
    setPendingDelete({ type: 'single', students: [student] });
  };

  const confirmBulkDelete = () => {
    if (selectedStudents.length === 0) return;
    setPendingDelete({ type: 'bulk', students: selectedStudents });
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      const ids = pendingDelete.students.map((student) => student.id);
      if (pendingDelete.type === 'single') {
        await deleteStudent(ids[0]);
        toast.success('Student deleted');
      } else {
        const { data } = await bulkActionStudents({ ids, action: 'delete' });
        toast.success(data?.message || `${ids.length} students deleted`);
      }
      setPendingDelete(null);
      setSelectedIds(new Set());
      await loadStudents();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not delete student records');
    } finally {
      setDeleting(false);
    }
  };

  const openAddDialog = () => {
    setAddForm(emptyAddForm);
    setAddErrors({});
    setAddResult(null);
    setAddOpen(true);
  };

  const closeAddDialog = () => {
    if (addingStudent) return;
    setAddOpen(false);
    setAddResult(null);
    setAddErrors({});
  };

  const updateAddForm = (key) => (eventOrValue) => {
    const value = typeof eventOrValue === 'string' ? eventOrValue : eventOrValue.target.value;
    setAddForm((prev) => ({ ...prev, [key]: value }));
    setAddErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validateAddForm = () => {
    const errors = {};
    if (!addForm.name.trim()) errors.name = 'Name is required.';
    if (!EMAIL_RE.test(addForm.email.trim())) errors.email = 'Enter a valid email address.';
    setAddErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddStudent = async () => {
    if (!validateAddForm()) return;
    const payload = {
      name: addForm.name.trim(),
      email: addForm.email.trim().toLowerCase(),
      phone: addForm.phone.trim(),
      dob: addForm.dob,
      gender: addForm.gender.trim(),
      program_name: selectPayload(addForm.program_name).trim(),
      start_date: addForm.start_date,
      enrollment_status: selectPayload(addForm.enrollment_status),
      campus: selectPayload(addForm.campus),
      delivery_method: selectPayload(addForm.delivery_method),
      agent_id: selectPayload(addForm.agent_id) || null,
    };
    try {
      setAddingStudent(true);
      const { data } = await createStudent(payload);
      setAddResult(data);
      toast.success('Student account created');
      await loadStudents();
    } catch (err) {
      console.error(err);
      const message = cleanError(err, 'Could not create student');
      if (err?.response?.status === 409) {
        setAddErrors((prev) => ({ ...prev, email: message }));
      } else {
        toast.error(message);
      }
    } finally {
      setAddingStudent(false);
    }
  };

  const copyTempPassword = async (password) => {
    if (!password) return;
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Temp password copied');
    } catch {
      toast.error('Copy failed. Select and copy manually.');
    }
  };

  const openImportDialog = () => {
    setImportState(emptyImportState);
    setImportOpen(true);
  };

  const closeImportDialog = async () => {
    if (previewingImport || committingImport) return;
    const shouldRefresh = importState.step === 'result';
    setImportOpen(false);
    setImportState(emptyImportState);
    if (shouldRefresh) await loadStudents();
  };

  const setImportFile = (file) => {
    setImportState((prev) => ({ ...prev, file, error: '', preview: null, result: null }));
  };

  const handlePreviewImport = async () => {
    if (!importState.file) {
      setImportState((prev) => ({ ...prev, error: 'Choose a .xlsx or .csv file first.' }));
      return;
    }
    try {
      setPreviewingImport(true);
      setImportState((prev) => ({ ...prev, error: '' }));
      const { data } = await previewStudentImport(importState.file);
      setImportState((prev) => ({ ...prev, step: 'preview', preview: data, result: null }));
    } catch (err) {
      console.error(err);
      setImportState((prev) => ({ ...prev, error: cleanError(err, 'Could not preview this file.') }));
    } finally {
      setPreviewingImport(false);
    }
  };

  const handleCommitImport = async () => {
    const createRows = (importState.preview?.rows || []).filter((row) => row.action === 'create');
    if (createRows.length === 0) return;
    try {
      setCommittingImport(true);
      setImportState((prev) => ({ ...prev, error: '' }));
      const { data } = await commitStudentImport(createRows);
      setImportState((prev) => ({ ...prev, step: 'result', result: data }));
      toast.success(`Created ${data.created?.length || 0} students`);
    } catch (err) {
      console.error(err);
      setImportState((prev) => ({ ...prev, error: cleanError(err, 'Could not create students.') }));
    } finally {
      setCommittingImport(false);
    }
  };

  const columns = [
    {
      id: 'student_number',
      header: 'Student ID',
      sortable: true,
      sortAccessor: (student) => student.student_number || '',
      cellClassName: 'min-w-[130px] text-sm font-medium text-[#062B1A]',
      cell: (student) => student.student_number || 'Not assigned',
    },
    {
      id: 'name',
      header: 'Student Name',
      sortable: true,
      sortAccessor: (student) => student.name || '',
      cellClassName: 'min-w-[210px]',
      cell: (student) => (
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#062B1A] text-white flex items-center justify-center text-xs font-semibold">
            {studentInitials(student.name)}
          </div>
          <div>
            <p className="text-sm font-semibold text-[#062B1A]">{student.name || 'Unnamed student'}</p>
            <p className="text-xs text-[#062B1A]/55">{student.email}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'program',
      header: 'Program',
      sortable: true,
      sortAccessor: (student) => student.program_name || '',
      cellClassName: 'min-w-[160px] text-sm text-[#062B1A]/70',
      cell: (student) => student.program_name || 'Program not assigned',
    },
    {
      id: 'enrollment_status',
      header: 'Status',
      sortable: true,
      sortAccessor: (student) => student.enrollment_status || '',
      cellClassName: 'min-w-[150px]',
      cell: (student) => (
        <Select
          value={student.enrollment_status || NONE_VALUE}
          disabled={savingInline === `${student.id}:enrollment_status`}
          onValueChange={(value) =>
            handleInlineUpdate(student, { enrollment_status: value === NONE_VALUE ? '' : value }, 'enrollment_status')
          }
        >
          <SelectTrigger
            className="h-9 border-[#062B1A]/15"
            onClick={(event) => event.stopPropagation()}
          >
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>Not set</SelectItem>
            {studentMeta.enrollment_status.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      id: 'source',
      header: 'Source',
      sortable: true,
      sortAccessor: heardAboutSortValue,
      cellClassName: 'min-w-[170px]',
      cell: (student) => (
        <SourceCell
          record={student}
          onOpenAgency={(agentId) => navigate(`/admin/agencies/${agentId}`)}
        />
      ),
    },
    {
      id: 'start_date',
      header: 'Start Date',
      sortable: true,
      sortAccessor: (student) => student.start_date || '',
      cellClassName: 'min-w-[110px] text-sm text-[#062B1A]/70',
      cell: (student) => formatDate(student.start_date),
    },
    {
      id: 'end_date',
      header: 'Completion Date',
      sortable: true,
      sortAccessor: (student) => student.end_date || '',
      cellClassName: 'min-w-[110px] text-sm text-[#062B1A]/70',
      cell: (student) => formatDate(student.end_date),
    },
  ];

  const rowActions = (student) => (
    <div className="flex justify-end gap-1.5">
      <PortalEmailComposer
        audience="students"
        recipients={[student]}
        buttonLabel={`Email ${student.name || 'student'}`}
        buttonSize="icon"
        buttonClassName="border-[#062B1A]/15 text-[#062B1A]"
        iconOnly
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" variant="outline" size="icon" title="More actions" className="border-[#062B1A]/15 text-[#062B1A]">
            <MoreHorizontal className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{student.name}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => exportStudents('csv', [student.id])}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportStudents('pdf', [student.id])}>
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-700 focus:text-red-700" onClick={() => confirmSingleDelete(student)}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const bulkActions = ({ selectedCount }) => (
    <>
      <Button
        type="button"
        size="sm"
        onClick={openAddDialog}
        className="bg-[#062B1A] text-white hover:bg-[#041B10]"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Student
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={openImportDialog}
        className="border-[#062B1A]/15 text-[#062B1A]"
      >
        <Upload className="w-4 h-4 mr-2" />
        Import Students
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={selectedCount === 0}
        data-testid="students-bulk-export-csv"
        onClick={() => exportStudents('csv', selectedIds)}
        className="border-[#062B1A]/15 text-[#062B1A]"
      >
        <FileSpreadsheet className="w-4 h-4 mr-2" />
        Export CSV
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={selectedCount === 0}
        data-testid="students-bulk-export-pdf"
        onClick={() => exportStudents('pdf', selectedIds)}
        className="border-[#062B1A]/15 text-[#062B1A]"
      >
        <FileText className="w-4 h-4 mr-2" />
        Export PDF
      </Button>
      <Button
        type="button"
        size="sm"
        disabled={selectedCount === 0}
        onClick={confirmBulkDelete}
        data-testid="students-bulk-delete"
        className="bg-red-700 text-white hover:bg-red-800"
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Bulk Delete
      </Button>
      <PortalEmailComposer
        audience="students"
        recipients={students.filter((student) => selectedIds.has(student.id))}
        buttonLabel="Email selected"
        disabled={selectedCount === 0}
        buttonClassName="border-[#D4AF37]/45 text-[#7A5A05]"
      />
    </>
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-2">
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          Students
        </h2>
        <p className="text-sm text-[#062B1A]/60">
          Search, sort, edit, export, and clean up student records from one management table.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Students</span>
              <Users className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{summary.total}</p>
          </CardContent>
        </Card>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm gold-border-left">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Visible</span>
              <CalendarClock className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{summary.visible}</p>
          </CardContent>
        </Card>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Needs Attention</span>
              <AlertTriangle className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{summary.attention}</p>
          </CardContent>
        </Card>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">Deposit Overdue</span>
              <Wallet className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{summary.depositOverdue}</p>
          </CardContent>
        </Card>
      </div>

      <DataTable
        title="Student Management"
        actionsBelowSearch
        data={filteredStudents}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search anything..."
        filters={[
          {
            id: 'lifecycle',
            value: lifecycleFilter,
            onChange: setLifecycleFilter,
            options: lifecycleOptions,
          },
          {
            id: 'program',
            value: programFilter,
            onChange: setProgramFilter,
            options: programOptions,
          },
          {
            id: 'status',
            value: statusFilter,
            onChange: setStatusFilter,
            options: statusOptions,
          },
        ]}
        selectedIds={selectedIds}
        onSelectedIdsChange={setSelectedIds}
        bulkActions={bulkActions}
        rowActions={rowActions}
        onRowClick={(student) => navigate(`/admin/students/${student.id}`)}
        emptyMessage="No students match the current filters."
        pageSize={25}
      />

      <Dialog open={addOpen} onOpenChange={(open) => (open ? null : closeAddDialog())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Add Student</DialogTitle>
            <DialogDescription>
              Create a linked student login and student profile. Sensitive identifiers are added from the profile page.
            </DialogDescription>
          </DialogHeader>

          {!addResult ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={addForm.name} onChange={updateAddForm('name')} className={inputClass} />
                {addErrors.name && <p className="text-xs text-red-600">{addErrors.name}</p>}
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={addForm.email} onChange={updateAddForm('email')} className={inputClass} />
                {addErrors.email && <p className="text-xs text-red-600">{addErrors.email}</p>}
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={addForm.phone} onChange={updateAddForm('phone')} className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input type="date" value={addForm.dob} onChange={updateAddForm('dob')} className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Input value={addForm.gender} onChange={updateAddForm('gender')} className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label>Program</Label>
                <Select value={addForm.program_name} onValueChange={updateAddForm('program_name')}>
                  <SelectTrigger className={inputClass}><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>Not set</SelectItem>
                    {studentMeta.programs.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={addForm.start_date} onChange={updateAddForm('start_date')} className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label>Enrollment Status</Label>
                <Select value={addForm.enrollment_status} onValueChange={updateAddForm('enrollment_status')}>
                  <SelectTrigger className={inputClass}><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>Not set</SelectItem>
                    {studentMeta.enrollment_status.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Campus</Label>
                <Select value={addForm.campus} onValueChange={updateAddForm('campus')}>
                  <SelectTrigger className={inputClass}><SelectValue placeholder="Undecided" /></SelectTrigger>
                  <SelectContent>
                    {studentMeta.campus.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Method</Label>
                <Select value={addForm.delivery_method} onValueChange={updateAddForm('delivery_method')}>
                  <SelectTrigger className={inputClass}><SelectValue placeholder="Not set" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>Not set</SelectItem>
                    {studentMeta.delivery_method.map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Agency</Label>
                <Select value={addForm.agent_id} onValueChange={updateAddForm('agent_id')}>
                  <SelectTrigger className={inputClass}><SelectValue placeholder="No agency" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE_VALUE}>No agency</SelectItem>
                    {agentOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  Student account created
                </div>
                <p className="mt-1 text-sm text-[#062B1A]/65">{addResult.name} can sign in with {addResult.email}.</p>
              </div>
              <div className="space-y-2">
                <Label>Temporary Password</Label>
                <div className="flex gap-2">
                  <Input readOnly value={addResult.temp_password || ''} onFocus={(e) => e.target.select()} className={`${inputClass} font-mono`} />
                  <Button type="button" variant="outline" onClick={() => copyTempPassword(addResult.temp_password)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-[#062B1A]/50">This is shown once. Share it securely with the student.</p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeAddDialog} disabled={addingStudent}>
              {addResult ? 'Close' : 'Cancel'}
            </Button>
            {!addResult ? (
              <Button type="button" onClick={handleAddStudent} disabled={addingStudent} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                {addingStudent ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Student
              </Button>
            ) : (
              <Button type="button" onClick={() => navigate(`/admin/students/${addResult.id}`)} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                <Eye className="w-4 h-4 mr-2" />
                Open Profile
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(open) => (open ? null : closeImportDialog())}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Import Students</DialogTitle>
            <DialogDescription>
              Upload a CSV or XLSX file, preview validation results, then create only the valid new students.
            </DialogDescription>
          </DialogHeader>

          {importState.error && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{importState.error}</p>
          )}

          {importState.step === 'upload' && (
            <div className="space-y-4">
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#062B1A]/20 bg-[#F7F8FA] px-4 py-10 text-center hover:border-[#D4AF37]/60">
                <Upload className="mb-3 h-8 w-8 text-[#D4AF37]" />
                <span className="text-sm font-medium text-[#062B1A]">
                  {importState.file ? importState.file.name : 'Choose a .xlsx or .csv file'}
                </span>
                <span className="mt-1 text-xs text-[#062B1A]/50">Maximum 4 MB and 1000 rows.</span>
                <input
                  type="file"
                  accept=".xlsx,.csv"
                  className="hidden"
                  onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                />
              </label>
              <div className="flex flex-col gap-3 rounded-lg border border-[#062B1A]/10 p-3 text-sm text-[#062B1A]/65 sm:flex-row sm:items-center sm:justify-between">
                <span>Supported columns: {importHeaders.join(', ')}</span>
                <Button type="button" variant="outline" size="sm" onClick={downloadStudentTemplate} className="border-[#062B1A]/15 text-[#062B1A]">
                  <Download className="w-4 h-4 mr-2" />
                  Download template
                </Button>
              </div>
            </div>
          )}

          {importState.step === 'preview' && importState.preview && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className={badgeClass('success')}>{importState.preview.totals?.create || 0} will be created</Badge>
                <Badge variant="outline" className={badgeClass('neutral')}>{importState.preview.totals?.skip_duplicate || 0} skipped duplicates</Badge>
                <Badge variant="outline" className={badgeClass('danger')}>{importState.preview.totals?.error || 0} errors</Badge>
              </div>

              {importState.preview.ignored_columns?.length > 0 && (
                <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-3 text-sm text-[#8A6508]">
                  <p className="font-medium">Ignored columns</p>
                  <p className="mt-1">
                    {importState.preview.ignored_columns.map((column) => `${column.name}${column.sensitive ? ' (sensitive)' : ''}`).join(', ')}
                  </p>
                </div>
              )}

              <div className="max-h-[420px] overflow-auto rounded-lg border border-[#062B1A]/10">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-[#F7F8FA] text-xs uppercase tracking-[0.08em] text-[#062B1A]/45">
                    <tr>
                      <th className="p-3">Row</th>
                      <th className="p-3">Status</th>
                      <th className="p-3">Name</th>
                      <th className="p-3">Email</th>
                      <th className="p-3">Program</th>
                      <th className="p-3">Messages</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importState.preview.rows.map((row) => (
                      <tr key={row.row_number} className="border-t border-[#062B1A]/10 align-top">
                        <td className="p-3 text-[#062B1A]/60">{row.row_number}</td>
                        <td className="p-3">
                          <Badge variant="outline" className={badgeClass(row.action === 'create' ? 'success' : row.action === 'error' ? 'danger' : 'neutral')}>
                            {row.action === 'create' ? 'Create' : row.action === 'error' ? 'Error' : 'Skip duplicate'}
                          </Badge>
                        </td>
                        <td className="p-3 text-[#062B1A]">{row.data?.name}</td>
                        <td className="p-3 text-[#062B1A]/70">{row.data?.email}</td>
                        <td className="p-3 text-[#062B1A]/70">{row.data?.program_name}</td>
                        <td className="p-3 text-red-700">{(row.errors || []).join('; ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {importState.step === 'result' && importState.result && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-green-700">
                  <p className="text-2xl font-bold">{importState.result.created?.length || 0}</p>
                  <p className="text-sm">Created</p>
                </div>
                <div className="rounded-lg border border-[#062B1A]/10 bg-[#F7F8FA] p-4 text-[#062B1A]/70">
                  <p className="text-2xl font-bold">{importState.result.skipped?.length || 0}</p>
                  <p className="text-sm">Skipped</p>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
                  <p className="text-2xl font-bold">{importState.result.failed?.length || 0}</p>
                  <p className="text-sm">Failed</p>
                </div>
              </div>
              {(importState.result.failed?.length || 0) > 0 && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm font-semibold text-red-700">Failures</p>
                  <ul className="mt-2 space-y-1 text-sm text-red-700">
                    {importState.result.failed.map((row, index) => (
                      <li key={`${row.row_number}-${index}`}>Row {row.row_number}: {(row.errors || [row.reason]).join('; ')}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(importState.result.created?.length || 0) > 0 && (
                <div className="rounded-lg border border-[#D4AF37]/30 bg-[#D4AF37]/10 p-3 text-sm text-[#8A6508]">
                  Student accounts were created with one-time temporary passwords in the server response. Use individual profile reset workflows if a password needs to be reissued later.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeImportDialog} disabled={previewingImport || committingImport}>
              {importState.step === 'result' ? 'Close' : 'Cancel'}
            </Button>
            {importState.step === 'upload' && (
              <Button type="button" onClick={handlePreviewImport} disabled={previewingImport} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                {previewingImport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Preview
              </Button>
            )}
            {importState.step === 'preview' && (
              <Button
                type="button"
                onClick={handleCommitImport}
                disabled={committingImport || (importState.preview?.totals?.create || 0) === 0}
                className="bg-[#062B1A] text-white hover:bg-[#041B10]"
              >
                {committingImport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Create {importState.preview?.totals?.create || 0} students
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Delete {pendingDelete?.students?.length || 0} student{pendingDelete?.students?.length === 1 ? '' : 's'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected student record{pendingDelete?.students?.length === 1 ? '' : 's'},
              pulls them from enrolled courses, deletes their submissions and course grades, and clears converted application links.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={handleConfirmedDelete}
              className="bg-red-700 text-white hover:bg-red-800"
            >
              {deleting ? 'Deleting...' : `Delete ${pendingDelete?.students?.length || 0}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
